import React from "react";
import { Box, Typography } from "@mui/material";
import { PLACEMENT_BUCKET_COLORS } from "../../types/placement";
import { getApiBaseUrl, getS3Url } from "../../utils/imageUtils";
import { formatMetric } from "../ranking-card/rankingCardMetrics";
import {
  AssetKicker,
  AssetTitle,
  BRAND,
  Card,
  CardLabel,
  ChannelAvatar,
  DataTable,
  LinkedInAssetCanvas,
  MethodologyFooter,
  MUTED,
  NAVY,
  SANS,
  CARD_BORDER,
} from "./linkedinAssetChrome";

export type RadiographyPlacementCounts = {
  content: number;
  physical: number;
  banner: number;
  credits: number;
};

export type RadiographyDayCell = RadiographyPlacementCounts & {
  snapshotUrl: string | null;
  snapshotMentionId?: string | null;
};

export type RadiographyLane = {
  id: string;
  kind: "channel" | "rest";
  channelName: string;
  programNames: string;
  channelImageS3Key: string | null;
  channelImageUrl: string | null;
  channelColor: string | null;
  highlighted: boolean;
  views: number | null;
  episodeCount: number;
  mentions: RadiographyPlacementCounts;
  days: RadiographyDayCell[];
};

export type LinkedInBrandRadiographyData = {
  brandName: string;
  startDate: string;
  endDate: string;
  viewsAsOfLabel: string;
  days: Array<{ date: string; label: string }>;
  lanes: RadiographyLane[];
};

const MONTH_SHORT_ES = [
  "ENE",
  "FEB",
  "MAR",
  "ABR",
  "MAY",
  "JUN",
  "JUL",
  "AGO",
  "SEP",
  "OCT",
  "NOV",
  "DIC",
];
const MONTH_LONG_ES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

function ymdParts(ymd: string): { day: number; month: number } | null {
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return null;
  return { day: d, month: m };
}

function weekKicker(startDate: string, endDate: string): string {
  const start = ymdParts(startDate);
  const end = ymdParts(endDate);
  if (!start || !end) return "YOUTUBE";
  const month = MONTH_SHORT_ES[start.month - 1] ?? "";
  return `SEMANA ${start.day}–${end.day} ${month} · YOUTUBE`;
}

function weekMethodologyRange(startDate: string, endDate: string): string {
  const start = ymdParts(startDate);
  const end = ymdParts(endDate);
  if (!start || !end) return "";
  const month = MONTH_LONG_ES[end.month - 1] ?? "";
  return `el ${start.day} y el ${end.day} de ${month}`;
}

/** Same type scale as LinkedInLivestreamCompareAsset. */
const CHANNEL_AVATAR_PX = 44;
const NAME_PX = 22;
const META_PX = 14;
const HEADER_PX = 14;
/** 6 lanes + a metrics row each fill the 1080×1350 canvas with compare-like section gaps. */
const LANE_ROW_HEIGHT_PX = 62;
const METRICS_ROW_HEIGHT_PX = 52;
const LANE_FIT_COUNT = 6;
const SNAPSHOT_WIDTH_PX = 68;
const SNAPSHOT_HEIGHT_PX = 38;
const CARD_LABEL_MB = "16px";

function densityFor(laneCount: number): number {
  return Math.min(1, LANE_FIT_COUNT / Math.max(laneCount, 1));
}

function channelImageSrc(lane: RadiographyLane): string {
  if (lane.channelImageS3Key) return getS3Url(lane.channelImageS3Key);
  if (lane.channelImageUrl) return snapshotSrc(lane.channelImageUrl);
  return "";
}

function accentFor(lane: RadiographyLane, index: number): string {
  if (lane.channelColor && /^#[0-9a-fA-F]{6}$/.test(lane.channelColor)) {
    return lane.channelColor;
  }
  const palette = ["#7056EB", "#4ECDC4", "#F7B32B", "#FF6B6B", "#45B7D1", "#95E1A3"];
  return palette[index % palette.length]!;
}

function formatViews(views: number | null): string {
  if (views == null) return "—";
  return formatMetric(views, "views");
}

/** Route YouTube thumbs through the API so html2canvas can paint them (CORS). */
function snapshotSrc(url: string | null | undefined): string {
  if (!url) return "";
  try {
    const host = new URL(url).hostname;
    if (
      host === "i.ytimg.com" ||
      host === "img.youtube.com" ||
      host.endsWith(".ggpht.com") ||
      host.endsWith(".googleusercontent.com")
    ) {
      return `${getApiBaseUrl()}/api/proxy/remote-image?url=${encodeURIComponent(url)}`;
    }
  } catch {
    return url;
  }
  return url;
}

const MARK_COLOR = PLACEMENT_BUCKET_COLORS;

/** CSS shapes (not glyphs) so ●/■/▲ share a true vertical center with labels. */
const MarkFigure: React.FC<{
  bucket: keyof typeof MARK_COLOR;
  fontSize?: number;
}> = ({ bucket, fontSize = 14 }) => {
  const color = MARK_COLOR[bucket];
  if (bucket === "content") {
    const d = 6;
    return (
      <Box
        component="span"
        aria-hidden
        sx={{
          width: d,
          height: d,
          borderRadius: "50%",
          bgcolor: color,
          flexShrink: 0,
          display: "block",
        }}
      />
    );
  }
  if (bucket === "physical") {
    const d = 5;
    return (
      <Box
        component="span"
        aria-hidden
        sx={{
          width: d,
          height: d,
          borderRadius: "1px",
          bgcolor: color,
          flexShrink: 0,
          display: "block",
        }}
      />
    );
  }
  if (bucket === "banner") {
    return (
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 8,
          height: 4,
          borderRadius: "1px",
          bgcolor: color,
          flexShrink: 0,
          display: "block",
        }}
      />
    );
  }
  // Fixed 6px-tall triangle; stroke-linejoin rounds the corners.
  const h = 6;
  const w = 7;
  return (
    <Box
      component="svg"
      aria-hidden
      viewBox="0 0 7 6"
      sx={{
        width: w,
        height: h,
        flexShrink: 0,
        display: "block",
      }}
    >
      <path
        d="M3.5 1.1 L6.15 5.15 H0.85 Z"
        fill={color}
        stroke={color}
        strokeWidth={1.15}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </Box>
  );
};

const DayMarks: React.FC<{
  cell: RadiographyDayCell;
  compact?: boolean;
}> = ({ cell, compact = false }) => {
  const marks = (
    [
      { key: "content" as const, count: cell.content },
      { key: "physical" as const, count: cell.physical },
      { key: "banner" as const, count: cell.banner ?? 0 },
      { key: "credits" as const, count: cell.credits },
    ] as Array<{ key: keyof RadiographyPlacementCounts; count: number }>
  ).filter((m) => m.count > 0);

  if (marks.length === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: compact ? "6px" : "8px",
        flexWrap: "nowrap",
      }}
    >
      {marks.map(({ key, count }) => (
        <Box
          key={key}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "3px",
            fontFamily: SANS,
            fontWeight: 700,
            fontSize: compact ? 12 : HEADER_PX,
            lineHeight: 1,
            color: MARK_COLOR[key],
            height: compact ? 14 : 16,
          }}
        >
          <MarkFigure bucket={key} fontSize={compact ? 12 : 14} />
          {count > 1 ? (
            <Box
              component="span"
              sx={{
                fontSize: compact ? 11 : META_PX,
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1,
                color: MARK_COLOR[key],
                opacity: 0.85,
              }}
            >
              {count}
            </Box>
          ) : null}
        </Box>
      ))}
    </Box>
  );
};

const DayCell: React.FC<{
  cell: RadiographyDayCell;
  snapshotWidth: number;
  snapshotHeight: number;
}> = ({ cell, snapshotWidth, snapshotHeight }) => {
  const hasPresence =
    cell.content > 0 || cell.physical > 0 || cell.banner > 0 || cell.credits > 0;
  if (!hasPresence) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "3px",
        width: "100%",
        minWidth: 0,
      }}
    >
      {cell.snapshotUrl ? (
        <Box
          sx={{
            width: snapshotWidth,
            height: snapshotHeight,
            borderRadius: "5px",
            overflow: "hidden",
            flexShrink: 0,
            bgcolor: "rgba(255,255,255,0.06)",
          }}
        >
          <Box
            component="img"
            src={snapshotSrc(cell.snapshotUrl)}
            alt=""
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </Box>
      ) : null}
      <DayMarks cell={cell} compact={Boolean(cell.snapshotUrl)} />
    </Box>
  );
};

const SwimlaneChart: React.FC<{
  days: Array<{ date: string; label: string }>;
  lanes: RadiographyLane[];
  accents: string[];
  laneRowHeight: number;
  snapshotWidth: number;
  snapshotHeight: number;
}> = ({ days, lanes, accents, laneRowHeight, snapshotWidth, snapshotHeight }) => {
  // One grid for header + lanes so date tracks and snapshot cells share widths.
  // Day columns are flush (no columnGap) so the 1px divider is the only gutter —
  // a 12px gap after each day sat to the right of the thumb and pulled it off-center.
  const avatarColPx = CHANNEL_AVATAR_PX + 4;
  const gridColumns = `${avatarColPx}px 12px minmax(0, 2.8fr) 12px repeat(${days.length}, minmax(0, 1fr))`;
  const dayRule = (dayIndex: number) =>
    dayIndex === 0 ? "none" : `1px solid ${CARD_BORDER}`;
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridColumns,
          gridTemplateRows: `auto repeat(${lanes.length}, ${laneRowHeight}px)`,
          columnGap: 0,
          rowGap: "2px",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            gridColumn: "1 / 5",
            alignSelf: "end",
            pb: "10px",
            mb: "2px",
            borderBottom: `1px solid ${CARD_BORDER}`,
          }}
        />
        {days.map((day, dayIndex) => (
          <Typography
            key={day.date}
            sx={{
              m: 0,
              width: "100%",
              boxSizing: "border-box",
              alignSelf: "end",
              pb: "10px",
              mb: "2px",
              borderBottom: `1px solid ${CARD_BORDER}`,
              borderLeft: dayRule(dayIndex),
              fontFamily: SANS,
              fontWeight: 600,
              fontSize: HEADER_PX,
              lineHeight: 1.15,
              color: MUTED,
              textAlign: "center",
            }}
          >
            {day.label}
          </Typography>
        ))}
        {lanes.map((lane, index) => {
          const accent = accents[index]!;
          return (
            <React.Fragment key={lane.id}>
              {lane.kind === "channel" ? (
                <ChannelAvatar
                  src={channelImageSrc(lane)}
                  accentColor={accent}
                  size={CHANNEL_AVATAR_PX}
                  sx={{ border: "none" }}
                />
              ) : (
                <Box sx={{ width: CHANNEL_AVATAR_PX }} />
              )}
              <Box />
              <Box
                sx={{
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: "2px",
                }}
              >
                <Typography
                  sx={{
                    m: 0,
                    fontFamily: SANS,
                    fontWeight: 600,
                    fontSize: NAME_PX,
                    lineHeight: 1.1,
                    color: NAVY,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {lane.channelName}
                </Typography>
                {lane.kind !== "rest" && lane.programNames ? (
                  <Typography
                    sx={{
                      m: 0,
                      fontFamily: SANS,
                      fontWeight: 500,
                      fontSize: META_PX,
                      lineHeight: 1,
                      color: MUTED,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {lane.programNames}
                  </Typography>
                ) : null}
              </Box>
              <Box />
              {lane.days.map((cell, dayIndex) => (
                <Box
                  key={`${lane.id}-${days[dayIndex]!.date}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    minWidth: 0,
                    height: "100%",
                    boxSizing: "border-box",
                    overflow: "hidden",
                    borderLeft: dayRule(dayIndex),
                  }}
                >
                  <DayCell
                    cell={cell}
                    snapshotWidth={snapshotWidth}
                    snapshotHeight={snapshotHeight}
                  />
                </Box>
              ))}
            </React.Fragment>
          );
        })}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "14px",
          mt: "5px",
          pt: "5px",
          borderTop: `1px solid ${CARD_BORDER}`,
        }}
      >
        {(
          [
            ["content", "Mención"],
            ["physical", "Placement"],
            ["banner", "Banner"],
            ["credits", "Créditos"],
          ] as const
        ).map(([bucket, label]) => (
          <Box
            key={label}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              height: 16,
            }}
          >
            <MarkFigure bucket={bucket} fontSize={14} />
            <Typography
              sx={{
                m: 0,
                fontFamily: SANS,
                fontWeight: 600,
                fontSize: META_PX,
                lineHeight: 1,
                color: MUTED,
              }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const LinkedInBrandRadiographyAsset: React.FC<{
  data: LinkedInBrandRadiographyData;
}> = ({ data }) => {
  const accents = data.lanes.map((lane, index) => accentFor(lane, index));
  const accentByLaneId = new Map(
    data.lanes.map((lane, index) => [lane.id, accents[index]!]),
  );
  const metricsLanes = [...data.lanes].sort((a, b) => {
    if (a.kind === "rest") return 1;
    if (b.kind === "rest") return -1;
    const av = a.views ?? -1;
    const bv = b.views ?? -1;
    if (bv !== av) return bv - av;
    return a.channelName.localeCompare(b.channelName);
  });
  const tableColumns = `${CHANNEL_AVATAR_PX}px minmax(0, 1.5fr) minmax(64px, 0.6fr) minmax(54px, 0.45fr) minmax(64px, 0.5fr) minmax(68px, 0.5fr) minmax(58px, 0.45fr) minmax(64px, 0.5fr)`;
  const brandName = data.brandName.trim() || "la marca";
  const density = densityFor(data.lanes.length);
  const laneRowHeight = Math.max(48, Math.round(LANE_ROW_HEIGHT_PX * density));
  const metricsRowHeight = Math.max(44, Math.round(METRICS_ROW_HEIGHT_PX * density));
  const snapshotHeight = Math.min(SNAPSHOT_HEIGHT_PX, laneRowHeight - 20);
  const snapshotWidth = Math.round(
    snapshotHeight * (SNAPSHOT_WIDTH_PX / SNAPSHOT_HEIGHT_PX),
  );

  return (
    <LinkedInAssetCanvas>
      <Box>
        <AssetKicker>{weekKicker(data.startDate, data.endDate)}</AssetKicker>
        <AssetTitle>
          {brandName} en YouTube, de TV a digital:
          <br />
          ¿Dónde apareció la semana pasada?
        </AssetTitle>
      </Box>

      <Card sx={{ pt: "24px", pb: "12px" }}>
        <CardLabel sx={{ mb: CARD_LABEL_MB }}>
          Presencia de {brandName}, día a día
        </CardLabel>
        <SwimlaneChart
          days={data.days}
          lanes={data.lanes}
          accents={accents}
          laneRowHeight={laneRowHeight}
          snapshotWidth={snapshotWidth}
          snapshotHeight={snapshotHeight}
        />
      </Card>

      <Card>
        <CardLabel sx={{ mb: CARD_LABEL_MB }}>Métricas consolidadas</CardLabel>
        <DataTable
          columns={tableColumns}
          nameFontSize={NAME_PX}
          metaFontSize={META_PX}
          rowHeight={metricsRowHeight}
          headers={[
            { label: "Canal", align: "left", gridColumn: "1 / 3" },
            { label: "Views", sorted: true, align: "right", gridColumn: 3 },
            { label: "Episodios", align: "right", gridColumn: 4 },
            { label: "Mención", align: "right", gridColumn: 5 },
            { label: "Placement", align: "right", gridColumn: 6 },
            { label: "Banner", align: "right", gridColumn: 7 },
            { label: "Créditos", align: "right", gridColumn: 8 },
          ]}
          rows={metricsLanes.map((lane) => ({
            key: lane.id,
            avatar:
              lane.kind === "channel" ? (
                <ChannelAvatar
                  src={channelImageSrc(lane)}
                  accentColor={accentByLaneId.get(lane.id) ?? BRAND}
                  size={CHANNEL_AVATAR_PX}
                  sx={{ border: "none" }}
                />
              ) : (
                <Box sx={{ width: CHANNEL_AVATAR_PX }} />
              ),
            name: lane.channelName,
            meta:
              lane.kind === "rest"
                ? undefined
                : lane.programNames || undefined,
            cells: [
              formatViews(lane.views),
              String(lane.episodeCount),
              String(lane.mentions.content),
              String(lane.mentions.physical),
              String(lane.mentions.banner),
              String(lane.mentions.credits),
            ],
          }))}
        />
      </Card>

      <MethodologyFooter>
        {`: apariciones de ${brandName} en episodios de YouTube entre ${weekMethodologyRange(data.startDate, data.endDate)}. Mención: se nombra en el audio. Placement: visible en el set. Banner: gráfico de patrocinio. Créditos: apertura, cierre o créditos. Views a ${data.viewsAsOfLabel}.`}
      </MethodologyFooter>
    </LinkedInAssetCanvas>
  );
};

export default LinkedInBrandRadiographyAsset;
